import { API_BASE_URL } from '../config/api';

// Interfaces for the API response
export interface VoiceStyle {
  detail: string;
  availableStyles: string[];
}

export interface SupportedLocale {
  [locale: string]: VoiceStyle;
}

export interface Voice {
  voiceId: string;
  displayName: string;
  locale: string;
  displayLanguage: string;
  accent: string;
  description: string;
  gender: string;
  availableStyles: string[];
  supportedLocales: SupportedLocale;
}

export interface VoicesResponse {
  message: string;
  voices: Voice[];
}

/**
 * Fetches available voices from the API
 * @returns Promise with the voices response
 */
export const getVoices = async (): Promise<VoicesResponse> => {
  const token = localStorage.getItem('authToken');
  try {
    const response = await fetch(`${API_BASE_URL}/generate/voice/getVoices`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch voices: ${response.status}`);
    }

    const data: VoicesResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching voices:', error);
    throw error;
  }
};

/**
 * Extracts unique languages from the voices response
 * @param voices Array of voices from the API
 * @returns Array of unique language names
 */
export const extractLanguages = (voices: Voice[]): string[] => {
  const uniqueLanguages = new Set<string>();

  voices.forEach((voice) => {
    uniqueLanguages.add(voice.displayLanguage);
  });

  return Array.from(uniqueLanguages);
};

// Import the new API service
import {
  generateAudioFromAPI,
  fetchAudioFile,
} from './textToSpeechAudioService';

interface AudioResponse {
  blob: Blob;
  audioLengthInSeconds: number;
}
/**
 * Generates audio from text using the selected voice
 * @param text Text to convert to speech
 * @param voiceId ID of the voice to use
 * @param lang Language code (not used with the API but kept for compatibility)
 * @returns Promise with the audio blob
 */
export const generateAudio = async (
  text: string,
  voiceId: string,
  style: string
): Promise<AudioResponse> => {
  try {
    // Generate audio using the API
    const audioResponse = await generateAudioFromAPI(text, voiceId, style);
    //   const audioResponse = {
    //     "message": "Voice generated successfully",
    //     "result": {
    //         "audioFile": "https://murf.ai/user-upload/one-day-temp/5866905b-1d6b-4e9d-9d00-20a09afe49a8.mp3?response-cache-control=max-age%3D604801&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Date=20250410T000000Z&X-Amz-SignedHeaders=host&X-Amz-Expires=259200&X-Amz-Credential=AKIA27M5532DYKBCJICE%2F20250410%2Fus-east-2%2Fs3%2Faws4_request&X-Amz-Signature=b73da9db60f480bdfd201991024872425e99157c2f6a8bf9ac9a4e1666cd06e0",
    //         "audioLengthInSeconds": 0.640998
    //     }
    // }

    // Fetch the audio file from the URL
    const audioBlob = await fetchAudioFile(audioResponse.result.audioFile);

    return {
      blob: audioBlob,
      audioLengthInSeconds: audioResponse.result.audioLengthInSeconds,
    };
  } catch (error) {
    console.error('Error generating audio:', error);
    throw error;
  }
};
